import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DATE_RANGES, fmtDate } from "@/lib/constants";
import { Link } from "react-router-dom";
import { DeliveryAlertsCard } from "@/components/delivery/DeliveryAlertsCard";
import {
  ArrowUpRight, Warning, ChartBar, ListChecks, ShieldCheck,
} from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import CodeHealthMini from "@/components/dashboard/CodeHealthMini";
import DemandMapCard from "@/components/dashboard/DemandMapCard";
import { TfCoverageCard } from "@/components/trustedform/TfCoverageCard";
import { LegalGoLiveReminder } from "@/components/legal/LegalGoLiveReminder";
import { GoLiveChecklist } from "@/components/golive/GoLiveChecklist";
import { LaunchGuide } from "@/components/golive/LaunchGuide";
import { ThankYouTestNotice } from "@/components/thankyou/ThankYouTestNotice";
import { BuyerRequestsCard } from "@/components/dashboard/BuyerRequestsCard";
import { EveningDigestCard } from "@/components/dashboard/EveningDigestCard";
import { NewUserWizardButton } from "@/components/user-wizard/NewUserWizardButton";

const TONE_CLASSES = {
  black: "bg-black text-white",
  danger: "bg-red-600 text-white",
  white: "bg-white",
};

function Metric({ label, value, sub, testId, tone = "black" }) {
  const bg = TONE_CLASSES[tone] || TONE_CLASSES.white;
  return (
    <div className={`brut-card p-5 ${bg}`} data-testid={testId}>
      <div className={`label-mono ${tone !== "white" ? "opacity-80" : ""}`}>{label}</div>
      <div className="font-chivo font-black text-4xl tracking-tighter leading-none mt-2">{value ?? "—"}</div>
      {sub && <div className="text-xs mt-2 opacity-80">{sub}</div>}
    </div>
  );
}

const HEADLINE_BY_ORG = {
  internal: "Operations overview",
  client: "Client overview",
  affiliate: "Affiliate overview",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [range, setRange] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [breakdowns, setBreakdowns] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get(`/dashboard/summary?range=${range}`).then((r) => setSummary(r.data));
    api.get(`/dashboard/breakdowns?range=${range}`).then((r) => setBreakdowns(r.data));
    api.get(`/leads?limit=8`).then((r) => setRecent(r.data.leads));
  }, [range]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-mono">Dashboard</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            {HEADLINE_BY_ORG[user?.org_type] || HEADLINE_BY_ORG.affiliate}
          </h1>
          <div className="mt-3"><NewUserWizardButton testId="dashboard-new-user-wizard-btn" /></div>
        </div>
        <div className="flex max-w-full items-center gap-1 overflow-x-auto border border-black bg-white" data-testid="range-picker">
          {DATE_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest whitespace-nowrap border-r border-black last:border-r-0 ${
                range === r.key ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {user?.org_type === "internal" && user?.role !== "media_buyer" && <DeliveryAlertsCard />}
      {["super_admin", "admin"].includes(user?.role) && user?.org_type === "internal" && <ThankYouTestNotice />}
      {user?.org_type === "internal" && <BuyerRequestsCard />}
      {user?.role === "super_admin" && user?.org_type === "internal" && <EveningDigestCard />}
      {user?.role === "super_admin" && user?.org_type === "internal" && <LaunchGuide />}
      {user?.role === "super_admin" && user?.org_type === "internal" && <GoLiveChecklist />}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-metrics">
        <Metric label="Leads received" value={summary?.leads?.total} testId="metric-total" />
        <Metric label="Accepted (validated)" value={summary?.leads?.accepted} testId="metric-accepted" />
        <Metric label="Delivered" value={summary?.leads?.delivered} testId="metric-delivered" tone="white" />
        <Metric label="Duplicates" value={summary?.leads?.duplicates} testId="metric-duplicates" tone="white" />

        <Metric label="Bad leads reported" value={summary?.bad_leads?.total} testId="metric-bad" tone="white" />
        <Metric label="Pending review" value={summary?.bad_leads?.pending} testId="metric-pending" tone="white" />
        <Metric label="Approved returns" value={summary?.bad_leads?.approved} testId="metric-approved" tone="white" />
        <Metric label="Bad-lead rate" value={`${summary?.bad_leads?.bad_rate_pct ?? 0}%`} testId="metric-rate" tone="danger" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
        <div className="brut-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="font-chivo font-bold uppercase tracking-tight">Recent leads</div>
            <Link to="/leads" className="text-[11px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors duration-100" data-testid="link-all-leads">
              All leads <ArrowUpRight className="inline" size={12} />
            </Link>
          </div>
          <table className="w-full mt-3 text-sm font-mono">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="py-2 text-left">ID</th>
                <th className="py-2 text-left">Name</th>
                <th className="py-2 text-left">State</th>
                <th className="py-2 text-left">Status</th>
                <th className="py-2 text-left">Received</th>
              </tr>
            </thead>
            <tbody>
              {recent?.map((l) => (
                <tr key={l.id} className="border-b border-zinc-200 hover:bg-zinc-100">
                  <td className="py-2 text-[11px]">{l.id.slice(0, 8)}</td>
                  <td className="py-2">
                    <Link to={`/leads/${l.id}`} className="hover:underline">
                      {l.first_name} {l.last_name}
                    </Link>
                  </td>
                  <td className="py-2">{l.state || "—"}</td>
                  <td className="py-2"><StatusBadge status={l.status} /></td>
                  <td className="py-2 text-[11px]">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
              {!recent?.length && (
                <tr><td colSpan={5} className="py-6 text-center label-mono">No leads yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="brut-card p-5">
          <div className="font-chivo font-bold uppercase tracking-tight">Top bad-lead reasons</div>
          <ul className="mt-3 space-y-2 text-sm font-mono">
            {(breakdowns?.by_reason || []).slice(0, 8).map((r) => (
              <li key={r.key} className="flex justify-between items-center border-b border-zinc-200 pb-1">
                <span className="truncate mr-2">{r.key || "—"}</span>
                <span className="font-bold">{r.count}</span>
              </li>
            ))}
            {!breakdowns?.by_reason?.length && (
              <li className="label-mono">Nothing reported yet</li>
            )}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <Link to="/leads?status=new" className="brut-card p-5 hover:brut-shadow transition-shadow duration-100" data-testid="quick-new-leads">
          <ListChecks size={24} weight="bold" />
          <div className="label-mono mt-3">Fresh queue</div>
          <div className="font-chivo font-black text-2xl uppercase tracking-tighter mt-1">New leads →</div>
        </Link>
        <Link to="/bad-leads?status=pending_review" className="brut-card p-5 hover:brut-shadow transition-shadow duration-100" data-testid="quick-review-queue">
          <Warning size={24} weight="bold" />
          <div className="label-mono mt-3">Needs review</div>
          <div className="font-chivo font-black text-2xl uppercase tracking-tighter mt-1">Return queue →</div>
        </Link>
        <Link to="/audit" className="brut-card p-5 hover:brut-shadow transition-shadow duration-100" data-testid="quick-audit">
          <ShieldCheck size={24} weight="bold" />
          <div className="label-mono mt-3">Compliance</div>
          <div className="font-chivo font-black text-2xl uppercase tracking-tighter mt-1">Audit log →</div>
        </Link>
      </div>

      {["super_admin", "admin"].includes(user?.role) && user?.org_type === "internal" && (
        <div className="mt-8">
          <LegalGoLiveReminder />
          <DemandMapCard />
          <div className="mt-8"><TfCoverageCard compact /></div>
        </div>
      )}

      {user?.role === "super_admin" && (
        <div className="mt-8">
          <CodeHealthMini />
        </div>
      )}
    </div>
  );
}
